import { NOYAU_TEXTS_QUERY_KEY } from '@/components/admin/reglages/noyau-texts-editor';
import { useAdminSession } from '@/lib/admin-session';
import {
  fetchCriterion,
  fetchExperience,
  fetchNoyauTexts,
  fetchPersona,
  fetchProtocol,
} from '@/lib/api';
import type { VersionSummary, VersionedKind } from '@matter_extractor/shared';
import { NOYAU_TEXTS_ID } from '@matter_extractor/shared';
import { useQuery } from '@tanstack/react-query';

/**
 * The versions of an object a reference may point at (vitrine.md §3.1), read
 * when a menu opens or a link row names the object - never for a list.
 *
 * The cards of a library carry the two pointed versions and nothing else, and a
 * menu lists EVERY version, most recent first, so it asks for the detail: the
 * same read, under the same key, as the editor of that object (`['criterion',
 * email, id]` and its four neighbours), so a version saved in a tab shows in a
 * menu opened in another without a second request. It is `enabled` only when a
 * caller needs it, which is what keeps ten cited criteria from being ten
 * requests at mount.
 *
 * `null` while the read is in flight or when it failed: a menu then lists
 * `latest` and the version it already shows, and never a refusal about a list it
 * could not draw. The texts of the noyaux are a singleton, read through the key
 * of Réglages > Noyaux, where a 404 is the empty state (N8) and reads as null.
 */

export type ReferenceVersions = {
  readonly versions: readonly VersionSummary[];
  readonly development: VersionSummary;
  readonly production: VersionSummary | null;
};

/** What every detail of the five kinds carries and a menu reads. */
type VersionsDetail = {
  readonly versions: readonly VersionSummary[];
  readonly development: VersionSummary;
  readonly production: VersionSummary | null;
};

function readDetail(kind: VersionedKind, id: string): Promise<VersionsDetail | null> {
  switch (kind) {
    case 'criterion':
      return fetchCriterion(id);
    case 'persona':
      return fetchPersona(id);
    case 'protocol':
      return fetchProtocol(id);
    case 'experience':
      return fetchExperience(id);
    case 'noyau-texts':
      return fetchNoyauTexts();
    // The settings are cited by nothing: they have no reference to list versions for.
    case 'models':
    case 'rules':
    case 'method':
      return Promise.resolve(null);
  }
}

export function useReferenceVersions(
  kind: VersionedKind,
  id: string,
  enabled: boolean,
): ReferenceVersions | null {
  const { email } = useAdminSession();
  const read = useQuery({
    queryKey: [kind === 'noyau-texts' ? NOYAU_TEXTS_QUERY_KEY : kind, email, id],
    // The RAW detail is what the cache holds, under the key the object's own
    // editor writes: a second shape under that key would be the trap of issue
    // #16 (two readers agreeing on a key and disagreeing on what is under it).
    queryFn: () => readDetail(kind, id),
    select: (detail): ReferenceVersions | null =>
      detail === null
        ? null
        : {
            versions: detail.versions,
            development: detail.development,
            production: detail.production,
          },
    enabled: enabled && (kind !== 'noyau-texts' || id === NOYAU_TEXTS_ID),
    retry: false,
  });
  return read.data ?? null;
}
